// Tracking utilities for Google Tag Manager and form abandonment

let abandonmentTrackingInitialized = false;
let abandonmentEventSent = false; // Flag to prevent duplicate events
let lastAbandonmentEventTime = 0; // Timestamp of last abandonment event

/**
 * Save email when user enters it (for potential abandonment tracking)
 * @param {string} email - User email
 * @param {object} formData - Partial form data
 */
export function saveEmailForAbandonmentTracking(email, formData = {}) {
  if (!email || !email.includes('@')) {
    return;
  }

  const abandonmentData = {
    email,
    formData,
    timestamp: new Date().toISOString(),
    url: window.location.href,
    submitted: false // Flag to track if form was submitted
  };
  
  try {
    localStorage.setItem('form_abandonment_35', JSON.stringify(abandonmentData));
    
    // Initialize abandonment tracking only once
    if (!abandonmentTrackingInitialized) {
      initAbandonmentTracking();
      abandonmentTrackingInitialized = true;
    }
  } catch (error) {
    console.error('Error saving abandonment data:', error);
  }
}

/**
 * Initialize abandonment tracking - listens for page unload
 */
function initAbandonmentTracking() {
  // Track when user leaves the page
  const handlePageUnload = () => {
    // Prevent duplicate events - check both flag and timestamp (within 1 second)
    const now = Date.now();
    if (abandonmentEventSent || (now - lastAbandonmentEventTime < 1000)) {
      return;
    }
    
    try {
      const abandonmentData = localStorage.getItem('form_abandonment_35');
      if (abandonmentData) {
        const data = JSON.parse(abandonmentData);
        // Only track abandonment if form was NOT submitted
        if (!data.submitted && data.email) {
          abandonmentEventSent = true; // Mark as sent to prevent duplicates
          lastAbandonmentEventTime = now; // Update timestamp
          
          // NOTE: form_abandonment tracking în Google Analytics a fost eliminat
          // Datele sunt salvate doar în WordPress pentru rapoarte
          // Nu mai trimitem la GTM sau GA4 pentru că verifici direct din WP
          
          // Save to WordPress server (for email reports)
          // Use sendBeacon for reliable delivery on page unload
          const endpoint = '/wp-json/recorder/v1/save-abandonment-email';
          const payload = JSON.stringify({
            email: data.email,
            timestamp: data.timestamp,
            url: data.url,
            formData: data.formData || {}
          });
          
          // Try sendBeacon first (most reliable for page unload)
          if (navigator.sendBeacon) {
            const blob = new Blob([payload], { type: 'application/json' });
            navigator.sendBeacon(endpoint, blob);
          } else {
            // Fallback to fetch with keepalive
            fetch(endpoint, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: payload,
              keepalive: true // Keep request alive even after page unload
            }).catch(err => {
              console.error('Error saving abandonment email to server:', err);
            });
          }
        }
      }
    } catch (error) {
      console.error('Error tracking form abandonment:', error);
    }
  };

  // Track when page becomes hidden (user switches tab, closes browser, etc.)
  // But only if beforeunload/unload haven't fired yet
  const handleVisibilityChange = () => {
    if (document.hidden && !abandonmentEventSent) {
      // Use a small delay to avoid conflicts with beforeunload/unload
      setTimeout(() => {
        if (!abandonmentEventSent) {
          handlePageUnload();
        }
      }, 100);
    }
  };

  // Listen for page unload events
  // Use capture phase for beforeunload to ensure it fires first
  window.addEventListener('beforeunload', handlePageUnload, { capture: true });
  window.addEventListener('unload', handlePageUnload);
  document.addEventListener('visibilitychange', handleVisibilityChange);
}

/**
 * Mark form as submitted (prevents abandonment tracking)
 */
export function markFormAsSubmitted() {
  try {
    const abandonmentData = localStorage.getItem('form_abandonment_35');
    if (abandonmentData) {
      const data = JSON.parse(abandonmentData);
      data.submitted = true;
      localStorage.setItem('form_abandonment_35', JSON.stringify(data));
    }
    // Reset flag when form is submitted
    abandonmentEventSent = false;
  } catch (error) {
    console.error('Error marking form as submitted:', error);
  }
}

/**
 * Track form field interaction
 * @param {string} fieldName - Name of the field
 * @param {string} action - Action type (focus, blur, change)
 */
export function trackFieldInteraction(fieldName, action) {
  if (typeof window.dataLayer !== 'undefined') {
    window.dataLayer.push({
      event: 'form_field_interaction',
      eventCategory: 'Form 3.5%',
      eventAction: action,
      eventLabel: fieldName
    });
  }
}

/**
 * Track form step completion (for wizard)
 * @param {number} step - Step number
 */
export function trackStepCompletion(step) {
  if (typeof window.dataLayer !== 'undefined') {
    window.dataLayer.push({
      event: 'form_step_completed',
      eventCategory: 'Form 3.5%',
      eventAction: 'step_completed',
      eventLabel: `Step ${step}`
    });
  }
}

/**
 * Track form submission start
 */
export function trackFormSubmissionStart() {
  if (typeof window.dataLayer !== 'undefined') {
    window.dataLayer.push({
      event: 'form_submission_start',
      eventCategory: 'Form 3.5%',
      eventAction: 'submit_started'
    });
  }
  
  // Mark form as submitted to prevent abandonment tracking
  markFormAsSubmitted();
}

/**
 * Track form submission success
 */
export function trackFormSubmissionSuccess() {
  if (typeof window.dataLayer !== 'undefined') {
    // New event name
    window.dataLayer.push({
      event: 'form_submission_success',
      eventCategory: 'Form 3.5%',
      eventAction: 'submit_success'
    });
    
    // Legacy event name for compatibility with existing Analytics setup
    window.dataLayer.push({
      event: 'completare_formular',
      eventCategory: 'Form 3.5%',
      eventAction: 'submit_success'
    });
  }
  
  // Mark form as submitted to prevent abandonment tracking
  markFormAsSubmitted();
  
  // Clear abandonment data on success
  try {
    localStorage.removeItem('form_abandonment_35');
    // Reset flag when form is successfully submitted
    abandonmentEventSent = false;
  } catch (error) {
    console.error('Error clearing abandonment data:', error);
  }
}

/**
 * Track form submission error
 * @param {string} error - Error message
 */
export function trackFormSubmissionError(error) {
  if (typeof window.dataLayer !== 'undefined') {
    window.dataLayer.push({
      event: 'form_submission_error',
      eventCategory: 'Form 3.5%',
      eventAction: 'submit_error',
      eventLabel: error
    });
  }
}

